import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import tokens from "src/shared/theme/tokens";
import { isPlainLeftClick, resolveBannerHref } from "../bannerLink";
import type { BannerSlide } from "./campaigns";

/** How long each banner stays on screen before rotating. */
export const ROTATE_INTERVAL_MS = 5000;

/** A horizontal swipe shorter than this is treated as a tap, not a drag. */
const SWIPE_THRESHOLD_PX = 40;

interface SlideImageProps {
  slide: BannerSlide;
  onExhausted: (key: string) => void;
}

/**
 * One slide's image, walking its fallback chain. When every source has failed
 * the slide reports itself exhausted so the carousel can drop it from the
 * rotation, which is how a property with no uploaded banner falls through to
 * campaigns only.
 */
const SlideImage = ({ slide, onExhausted }: SlideImageProps) => {
  const [stage, setStage] = useState(0);
  const sourceKey = slide.sources.join("|");

  useEffect(() => {
    setStage(0);
  }, [sourceKey]);

  const src = slide.sources[stage];

  useEffect(() => {
    if (src === undefined) onExhausted(slide.key);
  }, [src, slide.key, onExhausted]);

  if (src === undefined) return null;

  return (
    <img
      src={src}
      alt={slide.alt}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
      }}
      onError={() => setStage((s) => s + 1)}
    />
  );
};

export interface BannerCarouselProps {
  slides: BannerSlide[];
  /** Override the rotation interval. Mainly for tests. */
  intervalMs?: number;
  sx?: object;
}

/**
 * Full-width 4:1 home banner strip that rotates horizontally through its slides.
 *
 * A single slide renders exactly as the old single PropertyBanner did: no dots,
 * no timer, no controls. Rotation only kicks in once there are two or more.
 *
 * Auto-advance pauses on hover, on keyboard focus, and while the tab is hidden,
 * and is disabled entirely under prefers-reduced-motion so the banner never
 * moves on its own for residents who asked for that.
 */
export const BannerCarousel = ({
  slides,
  intervalMs = ROTATE_INTERVAL_MS,
  sx,
}: BannerCarouselProps) => {
  const navigate = useNavigate();
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );

  const [failed, setFailed] = useState<string[]>([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const handleExhausted = useCallback((key: string) => {
    setFailed((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);

  const visible = slides.filter((s) => !failed.includes(s.key));
  const count = visible.length;
  // Modulo rather than clamping: a slide dropping out mid-rotation must not
  // leave the index pointing past the end.
  const active = count > 0 ? index % count : 0;

  // Auto-advance. Skipped for a single slide, while paused, and under
  // prefers-reduced-motion.
  useEffect(() => {
    if (count < 2 || paused || prefersReducedMotion) return undefined;
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % count),
      intervalMs
    );
    return () => window.clearInterval(id);
  }, [count, paused, prefersReducedMotion, intervalMs]);

  // Don't rotate in a background tab; residents shouldn't return to a banner
  // mid-transition having missed the others.
  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  if (count === 0) return null;

  const go = (next: number) => setIndex(((next % count) + count) % count);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (count < 2) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      go(active + 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      go(active - 1);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartX.current;
    touchStartX.current = null;
    if (startX === null || count < 2) return;
    const delta = e.changedTouches[0].clientX - startX;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    go(delta < 0 ? active + 1 : active - 1);
  };

  return (
    <Box
      role="region"
      aria-roledescription={count > 1 ? "carousel" : undefined}
      aria-label="Property announcements"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={handleKeyDown}
      onTouchStart={(e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={handleTouchEnd}
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "4 / 1",
        borderRadius: `${tokens.radius.md}px`,
        overflow: "hidden",
        mb: 3,
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          transform: `translateX(-${active * 100}%)`,
          transition: prefersReducedMotion ? "none" : "transform 0.5s ease",
        }}
      >
        {visible.map((slide, i) => {
          const { href, internalPath } = resolveBannerHref(slide.link);
          const image = (
            <SlideImage slide={slide} onExhausted={handleExhausted} />
          );

          // Same-origin destinations navigate in-app in the same tab; external
          // ones open in a new tab. A real href is kept either way for
          // accessibility and right-click / open-in-new-tab.
          const linkProps: Record<string, unknown> = internalPath
            ? {
                onClick: (e: React.MouseEvent) => {
                  if (!isPlainLeftClick(e)) return;
                  e.preventDefault();
                  navigate(internalPath);
                },
              }
            : { target: "_blank", rel: "noopener noreferrer" };

          return (
            <Box
              key={slide.key}
              // Off-screen slides are hidden from assistive tech and taken out
              // of the tab order so a keyboard user isn't dropped into a banner
              // they can't see.
              aria-hidden={i !== active}
              sx={{
                flex: "0 0 100%",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {href ? (
                <Box
                  component="a"
                  href={href}
                  tabIndex={i === active ? 0 : -1}
                  {...linkProps}
                  sx={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                    lineHeight: 0,
                  }}
                >
                  {image}
                </Box>
              ) : (
                image
              )}
            </Box>
          );
        })}
      </Box>

      {count > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 10,
            right: 12,
            display: "flex",
            alignItems: "center",
            gap: 1,
            // Banners can be light or dark, so the dots carry their own
            // backdrop rather than relying on contrast with the artwork.
            px: 1.25,
            py: 0.875,
            borderRadius: 99,
            backgroundColor: "rgba(24, 47, 51, 0.38)",
          }}
        >
          {visible.map((slide, i) => (
            <Box
              key={slide.key}
              component="button"
              type="button"
              aria-label={`Show banner ${i + 1} of ${count}`}
              aria-current={i === active}
              onClick={() => go(i)}
              sx={{
                width: 8,
                height: 8,
                p: 0,
                border: "none",
                borderRadius: "50%",
                cursor: "pointer",
                backgroundColor:
                  i === active ? tokens.colors.surface : "rgba(255,255,255,.5)",
                "&:focus-visible": {
                  outline: `2px solid ${tokens.colors.surface}`,
                  outlineOffset: 2,
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
